import type { TFunction } from "i18next";
import { useRef, useMemo } from "react";
import U2000SaveCancelDialog from "../../../../../u2000Core/components/dialog/U2000SaveCancelDialog";
import type { U2102RoleActionHandler } from "./U2102RoleActionHandlers";
import type { U2102LogicalUnit, U2102LogicalUnitAction } from "../../../../U2102Service";
import U2000ComboBoxBase from "../../../../../u2000Core/components/formControls/combobox/U2000ComboBoxBase";
import { U2000FormStateEnumEditMode } from "../../../../../u2000Core/reducer/formReducer/U2000FormState";

// Typage des props du composant
interface U2102RoleActionDlgProps {
  u2102RoleActionHandler: U2102RoleActionHandler;
  t: TFunction;
  NS: string;
}

// Composant pour le dialogue d'édition des actions d'une unité logique
function U2102RoleActionEditDlg({ u2102RoleActionHandler, t, NS }: U2102RoleActionDlgProps) {
  const { formStateHandlers } = u2102RoleActionHandler;

  // Ref for default focus on the logical unit combobox
  const defaultFocusRef = useRef<HTMLButtonElement>(null);

  // Unified handler that manages Save and Cancel differently
  const handleClose = (action: "save" | "cancel") => {
    if (action === "cancel") {
      // Temporarily disable onBlur validation to prevent unwanted validation on cancel
      const originalOnBlur = formStateHandlers.onBlur;
      formStateHandlers.onBlur = () => {
        /* Ignore onBlur during cancel */
      };

      // Close dialog immediately without validation
      u2102RoleActionHandler.onDlgClose("cancel");

      // Restore original onBlur handler after dialog closes
      setTimeout(() => {
        formStateHandlers.onBlur = originalOnBlur;
      }, 0);
    } else if (action === "save") {
      // Normal save flow with validation
      u2102RoleActionHandler.onDlgClose("save");
    }
  };

  const filteredLogicalUnitActions = useMemo(
    () =>
      u2102RoleActionHandler.logicalUnits.filter(
        (lu) => !u2102RoleActionHandler.gridLogicalUnitCodes.includes(lu.logicalUnitCode) || (formStateHandlers.getData() && lu.logicalUnitCode === formStateHandlers.getData().logicalUnitCode)
      ),
    [u2102RoleActionHandler.logicalUnits, u2102RoleActionHandler.gridLogicalUnitCodes, formStateHandlers]
  );

  // Calcul de l'état de la checkbox "Tout sélectionner"
  const areAllActionsChecked = useMemo(
    () =>
      u2102RoleActionHandler.logicalUnitActions.length > 0 &&
      u2102RoleActionHandler.logicalUnitActions.every((action) => action.idChecked),
    [u2102RoleActionHandler.logicalUnitActions]
  );

  // renmder
  return (
    <U2000SaveCancelDialog
      width="ax-w-2xl"
      height="h-[500px]"
      isOpen={u2102RoleActionHandler.isDialogOpen}
      onClose={handleClose}
      title={t("lblLogicalUnitAction", { ns: NS })}
      isValid={true}
      defaultFocusRef={defaultFocusRef}
    >
      <div className="flex flex-col gap-y-4">
        <U2000ComboBoxBase<U2102LogicalUnit>
          inputRef={defaultFocusRef as any}
          data={filteredLogicalUnitActions}
          keyField="id"
          descField="logicalUnitDescription"
          onChangeObject={(field) => u2102RoleActionHandler.onLogicalUnitChangeObject(field)}
          value={u2102RoleActionHandler.formStateHandlers.getData() as U2102LogicalUnit}
          disabled={u2102RoleActionHandler.formStateHandlers.getFormEditMode() === U2000FormStateEnumEditMode.EDIT}
        />

        {u2102RoleActionHandler.logicalUnitActions && u2102RoleActionHandler.logicalUnitActions.length > 0 && (
          <div className="mb-2 flex items-center gap-x-2 border-b pb-2">
            <div
              role="checkbox"
              aria-checked={areAllActionsChecked}
              tabIndex={0}
              className={`h-6 w-6 cursor-pointer rounded-sm border-2 transition-colors duration-300 ${areAllActionsChecked ? "border-blue-500 bg-blue-100" : "border-gray-300 bg-white"}`}
              onClick={() => u2102RoleActionHandler.onCheckAllLogicalUnitActions()}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  u2102RoleActionHandler.onCheckAllLogicalUnitActions();
                }
              }}
            >
              {areAllActionsChecked && (
                <svg className="m-auto h-5 w-5 text-black" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
                  <path d="M5 13l4 4L19 7" />
                </svg>
              )}
            </div>
            <p className="inline-block font-semibold text-gray-800">{t("lblSelectAll", { ns: NS })}</p>
          </div>
        )}

        {u2102RoleActionHandler.logicalUnitActions &&
          u2102RoleActionHandler.logicalUnitActions.map((action: U2102LogicalUnitAction) => (
            <div key={action.id} className="mb-1 flex items-center gap-x-2">
              <div
                role="checkbox"
                aria-checked={action.idChecked}
                tabIndex={0}
                className={`h-6 w-6 cursor-pointer rounded-sm border-2 transition-colors duration-300 ${action.idChecked ? "border-blue-500 bg-blue-100" : "border-gray-300 bg-white"}`}
                onClick={() => u2102RoleActionHandler.onCheckLogicalUnitAction(action)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    u2102RoleActionHandler.onCheckLogicalUnitAction(action);
                  }
                }}
              >
                {action.idChecked && (
                  <svg className="m-auto h-5 w-5 text-black" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
                    <path d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </div>
              <p className="inline-block text-gray-800">
                {action.logicalUnitActionCode} - {action.logicalUnitActionDescription}
              </p>
            </div>
          ))}
      </div>
    </U2000SaveCancelDialog>
  );
}

export default U2102RoleActionEditDlg;
